import { Button, TextField } from "@mui/material";
import FormHelperText from "@mui/material/FormHelperText";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../context/CurrencyContext.tsx";
import { NamedValue } from "../shared/CaseModel";
import { DialogShell } from "./general/DialogShell";
import { WeightedValuesEditor } from "./WeightedValuesEditor";

/** Room for an estimate with its weight beside it, in px. */
const WIDTH = 560;

export const AssetEditorDialog = ({
  asset,
  open,
  onClose,
}: {
  asset: NamedValue;
  open: boolean;
  onClose: (updatedAsset?: NamedValue) => void;
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "case.parties.assets.editDialog",
  });
  const { currency } = useCurrency();
  const [name, setName] = useState(asset.name);
  const [values, setValues] = useState(asset.values);

  const isNameEmpty = name === undefined || name.trim().length === 0;
  const isValueMissing = values === undefined || values.length === 0;

  return (
    <DialogShell
      open={open}
      onClose={() => onClose()}
      title={t("title")}
      description={t("description")}
      width={WIDTH}
      actions={
        <>
          <Button onClick={() => onClose()} color="inherit">
            {t("cancel")}
          </Button>
          <Button
            disabled={isNameEmpty || isValueMissing}
            onClick={() => {
              onClose({ ...asset, name, values });
            }}
            variant="contained"
          >
            {t("submit")}
          </Button>
        </>
      }
    >
      <TextField
        autoFocus
        required
        size="small"
        id="name"
        label={t("tip.name")}
        type="text"
        fullWidth
        variant="outlined"
        value={name}
        // pass name update
        onChange={(e) => setName(e.target.value)}
        error={isNameEmpty}
        helperText={isNameEmpty ? t("helper.emptyName") : undefined}
      />
      <div>
        <WeightedValuesEditor
          values={values}
          // pass value update
          onChange={setValues}
          min={0}
          currency={currency}
        />
        {isValueMissing && (
          <FormHelperText error>{t("helper.valueMissing")}</FormHelperText>
        )}
      </div>
    </DialogShell>
  );
};
